import { useEffect, useState } from "react";
import { cn } from "@volca/ui/lib/utils";
import type { ViewerBaseProps } from "./types";
import {
  ToolbarDivider,
  ViewerFrame,
  ViewerStatus,
  ViewerToolbar,
} from "./viewer-chrome";
import { ZoomControl, type ZoomValue } from "./zoom-control";

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4] as const;
const FIT = [{ value: "fit", label: "Fit" }] as const;

/** `data` as an object URL for the image, revoked when it changes. */
const useImageUrl = ({ src, data }: ViewerBaseProps) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!data) return;
    const next = URL.createObjectURL(new Blob([data]));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [data]);
  return data ? url : (src ?? null);
};

/**
 * A photo or scan as uploaded: fitted to the viewer, or at a zoom, scrolling
 * inside.
 */
export const ImageViewer = (props: ViewerBaseProps & { alt?: string }) => {
  const { className, toolbarActions, fileName, alt } = props;
  const url = useImageUrl(props);
  const [zoom, setZoom] = useState<ZoomValue>("fit");
  const [natural, setNatural] = useState<{ width: number; shown: number }>();
  const [failed, setFailed] = useState(false);
  const fit = zoom === "fit";
  const current = fit ? (natural?.shown ?? 1) : Number(zoom);

  return (
    <ViewerFrame className={className}>
      <ViewerToolbar
        end={
          <>
            <ZoomControl
              current={current}
              level={zoom}
              steps={ZOOM_STEPS}
              modes={FIT}
              disabled={!natural}
              onChange={setZoom}
            />
            {toolbarActions && (
              <>
                <ToolbarDivider />
                {toolbarActions}
              </>
            )}
          </>
        }
      />
      {failed || (!url && !props.data) ? (
        <ViewerStatus state="error" message="This image could not be shown." />
      ) : (
        <div className="relative min-h-0 flex-1 overflow-auto bg-surface-sunken">
          {url && (
            <div
              className={cn("p-4", {
                "absolute inset-0": fit,
                "grid min-h-full min-w-full place-items-center-safe": !fit,
              })}
            >
              <img
                src={url}
                alt={alt ?? fileName ?? "The uploaded image"}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  const width = image.naturalWidth || 1;
                  const height = image.naturalHeight || 1;
                  setNatural({
                    width,
                    shown: Math.min(
                      image.clientWidth / width,
                      image.clientHeight / height,
                    ),
                  });
                }}
                onError={() => setFailed(true)}
                className={cn("rounded-xs", {
                  "block size-full object-contain": fit,
                  "max-w-none": !fit,
                })}
                style={
                  fit || !natural
                    ? undefined
                    : { width: natural.width * current }
                }
              />
            </div>
          )}
        </div>
      )}
    </ViewerFrame>
  );
};

export default ImageViewer;
